import { forwardRef, memo, useRef } from 'react';
import { SpeechCapture } from './capture/SpeechCapture';
import {
  VoiceComposer,
  VoiceComposerHandle,
  VoiceComposerProps,
} from './composer/VoiceComposer';
import { useDictationTurn } from './functions/useDictationTurn';

export type DictationComposerProps = Omit<
  VoiceComposerProps,
  'voice' | 'onSend'
> & {
  /**
   * Words to go: held and let go on 发送, from the editor, or from the box's
   * send key (`sendOnReturn`); the box's words are the host's to clear.
   */
  onSend: (text: string, from: 'hold' | 'box' | 'editor') => void;
};

/**
 * The voice composer for any text, on chat-gateway's dictation
 * (`useDictationTurn`).
 */
export const DictationComposer = memo(
  forwardRef<VoiceComposerHandle, DictationComposerProps>(
    function DictationComposer({ onSend, ...props }, ref) {
      const send = useRef(onSend);
      send.current = onSend;
      const { voice, capture, endpoint } = useDictationTurn(text =>
        send.current(text, 'hold'),
      );
      return (
        <>
          <VoiceComposer
            ref={ref}
            {...props}
            voice={voice}
            onSend={(text, _voice, from) => send.current(text, from)}
          />
          {endpoint ? (
            <SpeechCapture
              ref={capture}
              endpoint={endpoint}
              onMessage={voice.receive}
            />
          ) : null}
        </>
      );
    },
  ),
);
